import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { publicApi } from '../services/api'
import LoadingSpinner from '../components/common/LoadingSpinner'

// TODO: Add logo dimension/quality validation specs when available
// (min height, width, image quality depending on full page, half page, or coupon)

const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'application/pdf', 'image/svg+xml']
const MAX_FILE_SIZE = 20 * 1024 * 1024 // 20MB

export default function SponsorLogoUpload() {
  const { submissionId } = useParams<{ submissionId: string }>()
  const [sponsorName, setSponsorName] = useState('')
  const [hasLogo, setHasLogo] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [uploaded, setUploaded] = useState(false)
  const [logoFileName, setLogoFileName] = useState('')

  useEffect(() => {
    async function fetchInfo() {
      if (!submissionId) return
      try {
        const data = await publicApi.getSponsorLogoInfo(submissionId)
        setSponsorName(data.sponsorName)
        setHasLogo(data.hasLogo)
      } catch {
        setError('Unable to load sponsor information. Please check the link and try again.')
      } finally {
        setLoading(false)
      }
    }
    fetchInfo()
  }, [submissionId])

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setUploadError(null)

    if (file.size > MAX_FILE_SIZE) {
      setUploadError('File size must be under 20MB')
      return
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      setUploadError('File must be PNG, JPG, SVG, or PDF')
      return
    }

    setUploading(true)
    try {
      // Get presigned URL
      const { uploadUrl, logoKey } = await publicApi.getSponsorLogoUploadUrl(
        file.name,
        file.type
      )

      // Upload to S3
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      })

      if (!uploadResponse.ok) {
        throw new Error('Upload failed')
      }

      // Save the logo key to the sponsorship record
      await publicApi.updateSponsorLogo(submissionId!, logoKey)

      setLogoFileName(file.name)
      setUploaded(true)
      setHasLogo(true)
    } catch {
      setUploadError('Failed to upload logo. Please try again.')
    } finally {
      setUploading(false)
    }
  }

  if (loading) {
    return (
      <div className="max-w-lg mx-auto py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-lg mx-auto py-12">
        <div className="card text-center">
          <div className="mb-4">
            <img
              src="/images/logo.png"
              alt="School Lunch Angels"
              className="h-16 mx-auto"
            />
          </div>
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-lg mx-auto py-8">
      <div className="card">
        {/* Header */}
        <div className="text-center mb-6">
          <img
            src="/images/logo.png"
            alt="School Lunch Angels"
            className="h-16 mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-sla-green">Logo Upload</h1>
          <p className="text-gray-600 mt-2">
            Angels on the Green Best Ball Scramble Golf Tournament
          </p>
          <p className="text-lg font-medium text-gray-800 mt-2">
            {sponsorName}
          </p>
        </div>

        {uploaded ? (
          /* Success State */
          <div className="text-center">
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-4">
              <svg className="w-12 h-12 text-green-500 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <p className="text-green-800 font-medium text-lg">Logo Uploaded Successfully!</p>
              <p className="text-green-700 text-sm mt-1">{logoFileName}</p>
            </div>
            <p className="text-sm text-gray-500">
              Thank you! Your logo will be included in the event program.
            </p>

            {/* Allow re-upload */}
            <div className="mt-6">
              <label className="btn btn-secondary cursor-pointer inline-block">
                Upload Different Logo
                <input
                  type="file"
                  className="hidden"
                  accept=".png,.jpg,.jpeg,.pdf,.svg"
                  onChange={handleFileUpload}
                  disabled={uploading}
                />
              </label>
            </div>
          </div>
        ) : hasLogo && !uploaded ? (
          /* Already has logo */
          <div className="text-center">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-4">
              <p className="text-blue-800 font-medium">A logo is already on file.</p>
              <p className="text-blue-700 text-sm mt-1">
                You can upload a replacement if needed.
              </p>
            </div>
            <label className="btn btn-primary cursor-pointer inline-block">
              {uploading ? 'Uploading...' : 'Replace Logo'}
              <input
                type="file"
                className="hidden"
                accept=".png,.jpg,.jpeg,.pdf,.svg"
                onChange={handleFileUpload}
                disabled={uploading}
              />
            </label>
          </div>
        ) : (
          /* Upload Form */
          <div>
            <p className="text-gray-600 mb-4">
              Please upload your company logo for inclusion in the event program.
            </p>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <label className="cursor-pointer">
                <div className="mb-3">
                  <svg className="w-10 h-10 text-gray-400 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className="text-sla-blue font-medium">
                  {uploading ? 'Uploading...' : 'Click to select your logo'}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  PNG, JPG, SVG, or PDF (max 20MB)
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  For best print quality, please provide your logo in CMYK color mode if available.
                </p>
                <input
                  type="file"
                  className="hidden"
                  accept=".png,.jpg,.jpeg,.pdf,.svg"
                  onChange={handleFileUpload}
                  disabled={uploading}
                />
              </label>
              {uploading && (
                <div className="mt-3">
                  <LoadingSpinner size="sm" />
                </div>
              )}
            </div>
          </div>
        )}

        {uploadError && (
          <p className="text-red-600 text-sm mt-3 text-center">{uploadError}</p>
        )}

        {/* Contact Info */}
        <div className="mt-6 pt-4 border-t text-center">
          <p className="text-sm text-gray-500">
            Questions? Contact us at{' '}
            <a href="mailto:secretary@schoollunchangels.org" className="text-sla-blue hover:underline">
              secretary@schoollunchangels.org
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}
